import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Phone, Mail, MapPin, Bed, Bath, Maximize, ChevronRight, Instagram, Facebook, Linkedin, Youtube, Toilet, Search, SlidersHorizontal, X } from 'lucide-react'
import { motion } from 'framer-motion'
import ChatWidget from '@/components/ChatWidget'

// Logo Orbe sin fondo
const ORBE_LOGO = 'https://raw.githubusercontent.com/ruxo-rod/files/main/Orbelogotipo.png'

interface Property {
  id: string
  title: string
  description: string
  price: number
  currency: string
  property_type: string
  purpose: string
  bedrooms?: number
  bathrooms?: number
  half_bathrooms?: number
  amenities?: string[]
  city?: string
  area_m2?: number
  zone: string
  address?: string
  featured_image?: string
  seo_title?: string
}

interface OrbeTemplateProps {
  agent: {
    name: string
    bio: string
    phone: string
    email?: string
    specialties: string[]
    zones: string[]
    avatar_url?: string
    social_media?: {
      facebook?: string
      instagram?: string
      linkedin?: string
      tiktok?: string
      youtube?: string
    }
  }
}

const OrbeTemplate: React.FC<OrbeTemplateProps> = ({ agent }) => {
  const navigate = useNavigate()
  const [properties, setProperties] = useState<Property[]>([])
  const [chatAutoOpen, setChatAutoOpen] = useState(false)
  const [perPage, setPerPage] = useState(6)
  const [showAbout, setShowAbout] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('orbe_visited') !== 'true'
    }
    return true
  })
  const [currentPage, setCurrentPage] = useState(1)
  // Filtros
  const [showFilters, setShowFilters] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterPurpose, setFilterPurpose] = useState('all')
  const [filterType, setFilterType] = useState('all')
  const [filterBedrooms, setFilterBedrooms] = useState('all')
  const [filterBathrooms, setFilterBathrooms] = useState('all')
  const [filterPriceMin, setFilterPriceMin] = useState('')
  const [filterPriceMax, setFilterPriceMax] = useState('')

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const subdomain = window.location.hostname.split('.')[0]
        const response = await fetch(`/api/agents/subdomain/${subdomain}/properties`)
        if (response.ok) {
          const data = await response.json()
          setProperties(data)
        }
      } catch (error) {
        console.error('Error fetching properties:', error)
      }
    }
    fetchProperties()

    // Auto-open chat después de 4 segundos
    const timer = setTimeout(() => {
      setChatAutoOpen(true)
    }, 4000)
    return () => clearTimeout(timer)
  }, [])

  // Marcar como visitado para colapsar "Sobre mí" en próximas visitas
  useEffect(() => {
    localStorage.setItem('orbe_visited', 'true')
  }, [])

  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, filterPurpose, filterType, filterBedrooms, filterBathrooms, filterPriceMin, filterPriceMax])

  const formatPrice = (price: number, currency: string) => {
    return `$${price.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ${currency}`
  }

  const getPurposeLabel = (purpose: string) => {
    const map: { [key: string]: string } = {
      'venta': 'Venta',
      'renta': 'Renta',
      'preventa': 'Preventa',
      'leasing': 'Leasing'
    }
    return map[purpose?.toLowerCase()] || 'Venta'
  }

  const handlePropertyClick = (seoTitle: string) => {
    navigate(`/property/${seoTitle}`)
  }

  // Formatear nombre: máximo 2 palabras por línea
  const formatName = (name: string) => {
    const words = name.split(' ')
    if (words.length <= 2) {
      return [name]
    }
    const lines = []
    for (let i = 0; i < words.length; i += 2) {
      lines.push(words.slice(i, i + 2).join(' '))
    }
    return lines
  }

  // Lógica de filtrado
  const filteredProperties = properties.filter(property => {
    const term = searchTerm.toLowerCase()
    const matchesSearch = !term ||
      property.title?.toLowerCase().includes(term) ||
      property.zone?.toLowerCase().includes(term) ||
      property.city?.toLowerCase().includes(term) ||
      property.description?.toLowerCase().includes(term) ||
      property.amenities?.some(a => a.toLowerCase().includes(term))

    const matchesPurpose = filterPurpose === 'all' || property.purpose?.toLowerCase() === filterPurpose
    const matchesType = filterType === 'all' || property.property_type === filterType
    const matchesBedrooms = filterBedrooms === 'all' || (property.bedrooms && property.bedrooms >= parseInt(filterBedrooms))
    const matchesBathrooms = filterBathrooms === 'all' || (property.bathrooms && property.bathrooms >= parseInt(filterBathrooms))
    const matchesPriceMin = !filterPriceMin || property.price >= parseFloat(filterPriceMin)
    const matchesPriceMax = !filterPriceMax || property.price <= parseFloat(filterPriceMax)

    return matchesSearch && matchesPurpose && matchesType && matchesBedrooms && matchesBathrooms && matchesPriceMin && matchesPriceMax
  })

  const activeFilterCount = [
    searchTerm,
    filterPurpose !== 'all' ? filterPurpose : '',
    filterType !== 'all' ? filterType : '',
    filterBedrooms !== 'all' ? filterBedrooms : '',
    filterBathrooms !== 'all' ? filterBathrooms : '',
    filterPriceMin,
    filterPriceMax
  ].filter(Boolean).length

  const clearAllFilters = () => {
    setSearchTerm('')
    setFilterPurpose('all')
    setFilterType('all')
    setFilterBedrooms('all')
    setFilterBathrooms('all')
    setFilterPriceMin('')
    setFilterPriceMax('')
  }

  const propertyTypes = [...new Set(properties.map(p => p.property_type).filter(Boolean))]
  const totalPages = perPage === 0 ? 1 : Math.ceil(filteredProperties.length / perPage)
  const paginatedProperties = perPage === 0 ? filteredProperties : filteredProperties.slice((currentPage - 1) * perPage, currentPage * perPage)

  return (
    <div className="min-h-screen bg-white font-sans">

      {/* ========== HEADER FIJO CON LOGO ========== */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#1a1a1a]/95 backdrop-blur-sm">
        <div className="container mx-auto px-4 lg:px-12 py-3 lg:py-4">
          <div className="flex items-center justify-between">
            <img
              src={ORBE_LOGO}
              alt="Orbe Bienes Raíces"
              className="h-14 lg:h-20 w-auto"
            />
            <a            
              href={`tel:${agent.phone}`}
              className="flex items-center text-[#C9A227] hover:text-white transition-colors font-medium text-sm lg:text-base"
            >
              <Phone size={16} className="mr-2" />
              <span className="hidden sm:inline">{agent.phone}</span>
            </a>
          </div>
        </div>
      </header>

      {/* ========== HERO SECTION - Layout Asimétrico ========== */}
      <section className="relative min-h-screen bg-[#1a1a1a] overflow-hidden pt-16 lg:pt-20">

        {/* LOGO ORBE GIGANTE DIFUMINADO DE FONDO - Solo desktop */}
        <div className="absolute inset-0 items-center justify-center pointer-events-none hidden lg:flex">
          <motion.img
            src={ORBE_LOGO}
            alt=""
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 0.04, scale: 1 }}
            transition={{ duration: 1.5, ease: "easeOut" }}
            className="w-[700px] xl:w-[800px] h-auto"
          />
        </div>

        <div className="container mx-auto px-4 lg:px-12 h-full relative z-10">
          <div className="flex flex-col lg:grid lg:grid-cols-12 gap-6 lg:gap-8 min-h-[calc(100vh-4rem)] lg:min-h-[calc(100vh-5rem)] items-center py-8 lg:py-0">

            {/* ===== INFO DEL AGENTE - 7 columnas en desktop ===== */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="z-10 order-2 lg:order-1 text-center lg:text-left lg:col-span-7 mt-8 lg:mt-0"
            >
              {/* Especialidades */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.6 }}
                className="text-[#C9A227] font-medium tracking-widest text-xs lg:text-sm mb-4 lg:mb-6"
              >
                {agent.specialties?.slice(0, 2).join(' | ').toUpperCase() || 'ASESOR INMOBILIARIO'}
              </motion.p>

              {/* NOMBRE */}
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.7 }}
                className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extralight text-white leading-tight mb-6 lg:mb-8"
              >
                {formatName(agent.name).map((line, i) => (
                  <span key={i} className="block">{line}</span>
                ))}
              </motion.h1>

              {/* Botón Contactar */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8, duration: 0.6 }}
                className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4"
              >
                <motion.a
                  href={`tel:${agent.phone}`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="flex items-center px-8 lg:px-10 py-3 lg:py-4 bg-[#C9A227] hover:bg-[#d4ad2f] text-white font-semibold tracking-wide rounded-full transition-all duration-300 shadow-lg hover:shadow-2xl"
                >
                  <Phone size={18} className="mr-2" />
                  LLAMAR
                </motion.a>
                <motion.a
                  href={`https://wa.me/${agent.phone.replace(/[^0-9]/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="flex items-center px-8 lg:px-10 py-3 lg:py-4 bg-[#25D366] hover:bg-[#1fb855] text-white font-semibold tracking-wide rounded-full transition-all duration-300 shadow-lg hover:shadow-2xl"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 mr-2"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  WHATSAPP
                </motion.a>
              </motion.div>

              {/* Datos de contacto */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1, duration: 0.6 }}
                className="space-y-3 flex flex-col items-center lg:items-start"
              >
                <a href={`tel:${agent.phone}`} className="flex items-center text-[#C9A227] hover:text-white transition-colors text-lg lg:text-xl font-medium">
                  <Phone size={20} className="mr-3 lg:mr-4" />
                  {agent.phone}
                </a>

                {agent.zones && agent.zones.length > 0 && (
                  <p className="flex items-center text-gray-400 text-base lg:text-lg">
                    <MapPin size={20} className="mr-3 lg:mr-4 text-[#C9A227]" />
                    {agent.zones.slice(0, 2).join(', ')}
                  </p>
                )}
              </motion.div>
            </motion.div>

            {/* ===== FOTO DEL AGENTE - 5 columnas en desktop ===== */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, duration: 1, ease: "easeOut" }}
              className="relative flex justify-center lg:justify-end items-center order-1 lg:order-2 w-full lg:col-span-5"
            >
              <div className="relative">
                {/* Foto principal - FORMA ORGÁNICA BLOB */}
                <motion.div
                  initial={{ borderRadius: "60% 40% 30% 70% / 60% 30% 70% 40%" }}
                  animate={{
                    borderRadius: [
                      "60% 40% 30% 70% / 60% 30% 70% 40%",
                      "30% 60% 70% 40% / 50% 60% 30% 60%",
                      "60% 40% 30% 70% / 60% 30% 70% 40%"
                    ]
                  }}
                  transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
                  className="relative w-[220px] h-[280px] sm:w-[260px] sm:h-[330px] lg:w-[320px] lg:h-[400px] xl:w-[360px] xl:h-[450px] overflow-hidden shadow-2xl"
                >
                  {agent.avatar_url ? (
                    <img
                      src={agent.avatar_url}
                      alt={agent.name}
                      className="w-full h-full object-cover object-top"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-[#2E4A7D] to-[#1a2d4a] flex items-center justify-center">
                      <span className="text-[80px] lg:text-[120px] text-white/20 font-extralight">
                        {agent.name.charAt(0)}
                      </span>
                    </div>
                  )}
                </motion.div>

                {/* Badge flotante con nombre y logo Orbe */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.2, duration: 0.6 }}
                  className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 bg-white rounded-xl shadow-2xl px-5 py-3 text-center z-20"
                >
                  <p className="text-[#1a1a1a] font-bold text-sm lg:text-base">{agent.name.split(' ')[0]}</p>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ========== SECCIÓN SOBRE MÍ ========== */}
      <div className="relative">
        <button
          onClick={() => setShowAbout(!showAbout)}
          className="w-full py-3 bg-[#fafafa] flex items-center justify-center gap-2 text-gray-500 hover:text-[#C9A227] transition-colors text-sm font-medium"
        >
          {showAbout ? 'Ocultar información del asesor ▲' : 'Ver información del asesor ▼'}
        </button>
      {showAbout && (
      <section className="py-16 lg:py-24 bg-[#fafafa] relative overflow-hidden">
        {/* Logo difuminado de fondo - solo desktop */}
        <div className="absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none opacity-[0.03] hidden lg:block">
          <img src={ORBE_LOGO} alt="" className="w-[500px] h-auto" />
        </div>

        <div className="container mx-auto px-4 lg:px-12 relative z-10">
          <div className="grid lg:grid-cols-3 gap-10 lg:gap-16">

            {/* Sidebar con datos */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="space-y-6 lg:space-y-8 lg:border-r lg:border-gray-200 lg:pr-12 order-2 lg:order-1"
            >
              {agent.specialties && agent.specialties.length > 0 && (
                <div>
                  <h4 className="text-xs font-bold text-[#C9A227] uppercase tracking-[0.2em] mb-2 lg:mb-3">Especialidades</h4>
                  <p className="text-gray-700 text-base lg:text-lg">{agent.specialties.join(' | ')}</p>
                </div>
              )}

              <div>
                <h4 className="text-xs font-bold text-[#C9A227] uppercase tracking-[0.2em] mb-2 lg:mb-3">Propiedades</h4>
                <p className="text-gray-700 text-base lg:text-lg">{properties.length} disponibles</p>
              </div>

              {agent.zones && agent.zones.length > 0 && (
                <div>
                  <h4 className="text-xs font-bold text-[#C9A227] uppercase tracking-[0.2em] mb-2 lg:mb-3">Área</h4>
                  <p className="text-gray-700 text-base lg:text-lg">{agent.zones.join(', ')}</p>
                </div>
              )}

              <div>
                <h4 className="text-xs font-bold text-[#C9A227] uppercase tracking-[0.2em] mb-2 lg:mb-3">Contactar</h4>
                <a href={`tel:${agent.phone}`} className="block text-[#2E4A7D] hover:text-[#C9A227] transition-colors text-base lg:text-lg font-medium">
                  {agent.phone}
                </a>

                {agent.social_media && (
                  <div className="flex space-x-4 mt-4">
                    {agent.social_media.instagram && (
                      <a href={agent.social_media.instagram} target="_blank" rel="noopener noreferrer"
                         className="w-10 h-10 bg-gray-100 hover:bg-[#C9A227] rounded-full flex items-center justify-center transition-all duration-300 group">
                        <Instagram size={18} className="text-gray-500 group-hover:text-white" />
                      </a>
                    )}
                    {agent.social_media.facebook && (
                      <a href={agent.social_media.facebook} target="_blank" rel="noopener noreferrer"
                         className="w-10 h-10 bg-gray-100 hover:bg-[#C9A227] rounded-full flex items-center justify-center transition-all duration-300 group">
                        <Facebook size={18} className="text-gray-500 group-hover:text-white" />
                      </a>
                    )}

                    {agent.social_media.tiktok && (
                      <a href={agent.social_media.tiktok} target="_blank" rel="noopener noreferrer"
                         className="w-10 h-10 bg-gray-100 hover:bg-[#C9A227] rounded-full flex items-center justify-center transition-all duration-300 group">
                        <svg viewBox="0 0 24 24" fill="currentColor" className="w-[18px] h-[18px] text-gray-500 group-hover:text-white"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.73a8.19 8.19 0 004.76 1.52v-3.4a4.85 4.85 0 01-1-.16z"/></svg>
                      </a>
                    )}
                    {agent.social_media.youtube && (
                      <a href={agent.social_media.youtube} target="_blank" rel="noopener noreferrer"
                         className="w-10 h-10 bg-gray-100 hover:bg-[#C9A227] rounded-full flex items-center justify-center transition-all duration-300 group">
                        <Youtube size={18} className="text-gray-500 group-hover:text-white" />
                      </a>
                    )}
                  </div>
                )}
              </div>
            </motion.div>

            {/* Bio completa */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              viewport={{ once: true }}
              className="lg:col-span-2 order-1 lg:order-2"
            >
              <h2 className="text-4xl lg:text-5xl font-extralight text-[#1a1a1a] mb-8 lg:mb-10">
                Sobre <span className="font-semibold">mí</span>
              </h2>

              {agent.bio ? (
                <p className="text-lg lg:text-xl text-gray-600 leading-8 lg:leading-9">{agent.bio}</p>
              ) : (
                <p className="text-lg lg:text-xl text-gray-500 italic leading-8 lg:leading-9">
                  Asesor inmobiliario profesional comprometido con encontrar la propiedad ideal para cada cliente.
                  Mi objetivo es brindarte una experiencia personalizada y cercana en cada paso del proceso.
                </p>
              )}
            </motion.div>
          </div>
        </div>
      </section>
      )}
      </div>

      {/* ========== PROPIEDADES ========== */}
      {properties.length > 0 && (
        <section className="py-16 lg:py-24 bg-white">
          <div className="container mx-auto px-4 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center mb-12 lg:mb-16"
            >
              <h2 className="text-4xl lg:text-5xl font-extralight text-[#1a1a1a]">
                Mis <span className="font-semibold">propiedades</span>
              </h2>
              <div className="w-16 lg:w-20 h-1 bg-[#C9A227] mx-auto mt-6"></div>
              {/* Contador de resultados */}
              {activeFilterCount > 0 && (
                <p className="text-sm text-gray-500 mt-4">
                  Mostrando {filteredProperties.length} de {properties.length} propiedades
                </p>
              )}

              {/* Barra de búsqueda + botón filtros */}
              <div className="flex flex-col sm:flex-row items-center gap-3 mt-8 max-w-2xl mx-auto">
                <div className="relative flex-1 w-full">
                  <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Buscar por nombre, zona, ciudad, amenidad..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-full text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#C9A227]/50 focus:border-[#C9A227]"
                  />
                  {searchTerm && (
                    <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      <X size={16} />
                    </button>
                  )}
                </div>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                    showFilters || activeFilterCount > 0
                      ? 'bg-[#C9A227] text-white shadow-md'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <SlidersHorizontal size={16} />
                  Filtros
                  {activeFilterCount > 0 && (
                    <span className="bg-white text-[#C9A227] text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                      {activeFilterCount}
                    </span>
                  )}
                </button>
              </div>

              {/* Panel de filtros expandible */}
              {showFilters && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-6 p-6 bg-[#fafafa] rounded-2xl border border-gray-100 max-w-4xl mx-auto"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {/* Propósito */}
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Propósito</label>
                      <div className="flex flex-wrap gap-2">
                        {['all', 'venta', 'renta', 'inversion', 'leasing'].map((p) => (
                          <button
                            key={p}
                            onClick={() => setFilterPurpose(p)}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                              filterPurpose === p
                                ? 'bg-[#C9A227] text-white'
                                : 'bg-white border border-gray-200 text-gray-600 hover:border-[#C9A227]'
                            }`}
                          >
                            {p === 'all' ? 'Todos' : p.charAt(0).toUpperCase() + p.slice(1)}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Tipo */}
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Tipo</label>
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => setFilterType('all')}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                            filterType === 'all'
                              ? 'bg-[#C9A227] text-white'
                              : 'bg-white border border-gray-200 text-gray-600 hover:border-[#C9A227]'
                          }`}
                        >
                          Todos
                        </button>
                        {propertyTypes.map((t) => (
                          <button
                            key={t}
                            onClick={() => setFilterType(t)}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                              filterType === t
                                ? 'bg-[#C9A227] text-white'
                                : 'bg-white border border-gray-200 text-gray-600 hover:border-[#C9A227]'
                            }`}
                          >
                            {t}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Recámaras */}
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Recámaras</label>
                      <div className="flex flex-wrap gap-2">
                        {['all', '1', '2', '3', '4'].map((n) => (
                          <button
                            key={n}
                            onClick={() => setFilterBedrooms(n)}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                              filterBedrooms === n
                                ? 'bg-[#C9A227] text-white'
                                : 'bg-white border border-gray-200 text-gray-600 hover:border-[#C9A227]'
                            }`}
                          >
                            {n === 'all' ? 'Todos' : n === '4' ? '4+' : n}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Baños */}
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Baños</label>
                      <div className="flex flex-wrap gap-2">
                        {['all', '1', '2', '3'].map((n) => (
                          <button
                            key={n}
                            onClick={() => setFilterBathrooms(n)}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                              filterBathrooms === n
                                ? 'bg-[#C9A227] text-white'
                                : 'bg-white border border-gray-200 text-gray-600 hover:border-[#C9A227]'
                            }`}
                          >
                            {n === 'all' ? 'Todos' : n === '3' ? '3+' : n}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Precio mínimo */}
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Precio mínimo</label>
                      <input
                        type="number"
                        placeholder="$ Mínimo"
                        value={filterPriceMin}
                        onChange={(e) => setFilterPriceMin(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-200 rounded-full text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#C9A227]/50 focus:border-[#C9A227]"
                      />
                    </div>

                    {/* Precio máximo */}
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Precio máximo</label>
                      <input
                        type="number"
                        placeholder="$ Máximo"
                        value={filterPriceMax}
                        onChange={(e) => setFilterPriceMax(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-200 rounded-full text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#C9A227]/50 focus:border-[#C9A227]"
                      />
                    </div>
                  </div>

                  {/* Botón limpiar */}
                  {activeFilterCount > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200 flex justify-center">
                      <button
                        onClick={clearAllFilters}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-[#C9A227] hover:bg-[#C9A227]/10 rounded-full transition-colors"
                      >
                        <X size={14} />
                        Limpiar todos los filtros
                      </button>
                    </div>
                  )}
                </motion.div>
              )}              
              
              <div className="flex items-center justify-center gap-3 mt-8">
                <span className="text-sm text-gray-500">Mostrar:</span>
                {[6, 9, 12, 0].map((n) => (
                  <button
                    key={n}
                    onClick={() => { setPerPage(n); setCurrentPage(1); }}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-300 ${
                      perPage === n
                        ? 'bg-[#C9A227] text-white shadow-md'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {n === 0 ? 'Todos' : n}
                  </button>
                ))}
              </div>
            </motion.div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-10">
            {paginatedProperties.map((property, index) => (              
                <motion.article
                  key={property.id}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  viewport={{ once: true }}
                  onClick={() => handlePropertyClick(property.seo_title || property.id)}
                  className="group cursor-pointer"
                >
                  <div className="relative aspect-[4/3] overflow-hidden rounded-xl lg:rounded-2xl mb-4 lg:mb-5">
                    {property.featured_image ? (
                      <img
                        src={property.featured_image}
                        alt={property.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                        <span className="text-5xl lg:text-6xl text-gray-400/50">🏠</span>
                      </div> 
                    )}
                    <div className="absolute inset-0 bg-[#2E4A7D]/0 group-hover:bg-[#2E4A7D]/20 transition-all duration-500"></div>

                    <div className="absolute top-3 left-3 lg:top-4 lg:left-4 bg-white/95 backdrop-blur-sm px-3 py-1.5 lg:px-4 lg:py-2 rounded-full text-xs lg:text-sm font-semibold text-[#2E4A7D]">
                      {getPurposeLabel(property.purpose)}
                    </div>

                    <div className="absolute bottom-3 right-3 lg:bottom-4 lg:right-4 bg-[#C9A227] text-white px-4 py-1.5 lg:px-5 lg:py-2 rounded-full font-bold text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      {formatPrice(property.price, property.currency)}
                    </div>
                  </div>

                  <p className="text-xs lg:text-sm text-[#C9A227] font-medium mb-1.5 lg:mb-2 tracking-wide">{property.zone}</p>

                  <h3 className="text-lg lg:text-xl font-semibold text-[#1a1a1a] group-hover:text-[#2E4A7D] transition-colors mb-2 lg:mb-3 line-clamp-2">
                    {property.title}
                  </h3>

                  <div className="flex items-center space-x-4 lg:space-x-5 text-gray-500 mb-3 lg:mb-4">
                    {property.bedrooms && (
                      <span className="flex items-center text-xs lg:text-sm">
                        <Bed size={14} className="mr-1.5 lg:mr-2" />
                        {property.bedrooms} hab
                      </span>
                    )}
                    {property.bathrooms && (
                      <span className="flex items-center text-xs lg:text-sm">
                        <Bath size={14} className="mr-1.5 lg:mr-2" />
                        {property.bathrooms} {property.bathrooms === 1 ? 'baño' : 'baños'}
                      </span>
                    )}
                    {property.half_bathrooms > 0 && (
                      <span className="flex items-center text-xs lg:text-sm">
                        <Toilet size={14} className="mr-1.5 lg:mr-2" />
                        {property.half_bathrooms} {property.half_bathrooms === 1 ? 'medio baño' : 'medios baños'}
                      </span>
                    )}
                    {property.area_m2 && (
                      <span className="flex items-center text-xs lg:text-sm">
                        <Maximize size={14} className="mr-1.5 lg:mr-2" />
                        {property.area_m2}m²
                      </span>
                    )}
                  </div>

                  <p className="text-xl lg:text-2xl font-bold text-[#2E4A7D]">
                    {formatPrice(property.price, property.currency)}
                  </p>

                  <div className="mt-3 lg:mt-4 flex items-center text-[#C9A227] font-semibold text-sm opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:translate-x-2">
                    Ver propiedad
                    <ChevronRight size={18} className="ml-1" />
                  </div>
                </motion.article>
              ))}
            </div>
            {perPage > 0 && totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-10">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 rounded-full text-sm font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                >
                  Anterior
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-9 h-9 rounded-full text-sm font-medium transition-all duration-300 ${
                      currentPage === page
                        ? 'bg-[#C9A227] text-white shadow-md'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 rounded-full text-sm font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                >
                  Siguiente
                </button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ========== CTA FINAL ========== */}
      <section className="py-16 lg:py-20 bg-[#1a1a1a] relative overflow-hidden">
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none hidden lg:block">
          <img src={ORBE_LOGO} alt="" className="w-[400px] h-auto opacity-[0.05]" />
        </div>

        <div className="container mx-auto px-4 lg:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="flex flex-col lg:flex-row items-center justify-between gap-6 lg:gap-8 text-center lg:text-left"
          >
            <div>
              <h3 className="text-2xl lg:text-3xl xl:text-4xl font-extralight text-white mb-2 lg:mb-3">
                ¿Buscas tu <span className="font-semibold">próximo hogar</span>?
              </h3>
              <p className="text-gray-400 text-base lg:text-lg">Permíteme ayudarte a encontrar la propiedad perfecta</p>
            </div>

            <motion.a
              href={`tel:${agent.phone}`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center px-8 lg:px-10 py-4 lg:py-5 bg-[#C9A227] hover:bg-[#d4ad2f] text-white font-semibold rounded-full transition-all duration-300 shadow-lg hover:shadow-2xl"
            >
              <Phone size={20} className="mr-3" />
              Llamar ahora
            </motion.a>
          </motion.div>
        </div>
      </section>

      {/* ========== FOOTER ========== */}
      <footer className="py-10 lg:py-12 bg-[#111]">
        <div className="container mx-auto px-4 lg:px-12">
          <div className="flex flex-col items-center text-center">
            <img
              src={ORBE_LOGO}
              alt="Orbe Bienes Raíces"
              className="h-14 lg:h-20 w-auto mb-4 lg:mb-6"
            />
            <p className="text-gray-500 text-xs lg:text-sm">
              © {new Date().getFullYear()} InnovAgent.tech | Todos los derechos reservados.
            </p>
          </div>
        </div>
      </footer>

      {/* Chat Widget - Esquina inferior izquierda, autoOpen con delay */}
      <div className="fixed bottom-4 left-4 z-50">
        <ChatWidget
          agentName={agent.name}
          agentSubdomain={window.location.hostname.split('.')[0]}
          agentAvatar={agent.avatar_url}
        agentPlan={agent.plan}
          autoOpen={chatAutoOpen}
        />
      </div>
    </div>
  )
}

export default OrbeTemplate
